/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';

import {
  Button, Form,
} from 'react-bootstrap';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Filters from './Components/Filters';

import fetchOrdersWrapper from './Functions/fetchOrders';
import markAsCompletedWrapper from './Functions/markAsCompleted';
import revertToConfirmedWrapper from './Functions/revertToConfirmed';
import archiveOrderWrapper from './Functions/archiveOrder';
import OrdersTable from './Components/OrdersTable';
import useModalState from '../../Hooks/useModalState';
import useCsvExport from '../../Hooks/useCsvExport';
import useUrlViewState from '../../Hooks/useUrlViewState';
import Error from '../../Components/Error';
import TripClaimsStandingNotice from '../../Components/TripClaims/TripClaimsStandingNotice';
import BookingService from '../../Services/BookingService';
import useAskAiSuggestion from '../../Components/ChatWidget/askAiSuggestions';
import { formatDateTime } from '../../Utils/timezone';

dayjs.extend(utc);

/**
 * Everything this screen remembers about itself, and therefore everything
 * that goes in the URL. The search and the page number are pulled out of the
 * filter set so that the address of an ordinary day's work reads
 * `/orders?quick_search=kovacs&page=3` rather than a blob of JSON; the rest
 * of the filters — arrays of statuses, a date range, a sort — have no such
 * tidy spelling and go in as one `filters` param.
 */
const ORDERS_VIEW = {
  quick_search: '',
  page: 1,
  filters: {},
};

// The search is debounced into the URL, and the URL is what drives the
// fetch, so the two waits are in series. Together they come to the half
// second the list has always waited after the last keystroke; the second is
// kept short because by the time it runs the typing has already stopped, and
// it now only has to collapse the double mount StrictMode does in
// development and the burst of a filter and a page arriving together.
const QUICK_SEARCH_DEBOUNCE_MS = 400;
const FETCH_DEBOUNCE_MS = 100;

/**
 * How the paused date range is read back to the reader. Budapest, because
 * that is the day the range was picked in — `formatDateTime` converts out of
 * the UTC instants the filter is stored as, so a range that reads
 * `2026-09-30T22:00:00.000Z` is still spoken of as 1 Oct.
 */
const PAUSED_RANGE_FORMAT = 'D MMM YYYY';

function Orders() {
  const {
    view, setView, params, dropParams,
  } = useUrlViewState(ORDERS_VIEW);

  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState(false);

  const orderDetailsModal = useModalState();
  const operatorInputModal = useModalState();
  const orderInputModal = useModalState();
  const driverInputModal = useModalState();
  const tripConfirmationModal = useModalState();
  const pickupGuideModal = useModalState();
  const addInvoiceModal = useModalState();
  // `/orders?booking=<id>&tab=<tab>` (the bell, trip-claim links) → Order Details on that tab.
  const [detailsRequest, setDetailsRequest] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * WHAT TO ASK ON THE ORDERS LIST. The list itself already finds a booking
   * by name, reference or flight — that is the search box, and the assistant
   * would only be a slower version of it. What this screen cannot do is look
   * across the day's data entry: `search_bookings` filters by CREATION date
   * and by `created_by` (the API tool's own docstring gives "what did Anita
   * upload today" as the example), and no filter on this page does.
   *
   * Static on purpose. The filters and the quick search are in the URL and
   * change constantly, and a prompt that chased them would read as a
   * different offer every few keystrokes.
   */
  useAskAiSuggestion({
    label: 'Ask about bookings',
    question: 'What was booked today, and by whom?',
    prompt: 'List the bookings created today — by creation date, not pickup date —'
      + ' grouped by the operator who entered them, with the reference, the customer,'
      + ' the pickup date and the partner for each.',
  });

  // The box has to answer every keystroke, so it keeps its own copy of the
  // text; the URL is only written once the typing settles.
  const [quickSearch, setQuickSearch] = useState(view.quick_search);

  const { page } = view;

  // The one object the API, the CSV export and the Filters panel all see —
  // the same shape this screen has always passed around, reassembled from
  // the three params it is now kept in.
  const filters = useMemo(() => {
    const assembled = { ...view.filters, page: view.page };
    if (view.quick_search) assembled.quick_search = view.quick_search;
    return assembled;
  }, [view]);

  /**
   * The date range a running quick search is ignoring, or null when nothing
   * is being ignored.
   *
   * A quick search is a hunt for ONE booking — a reference read out over the
   * phone, a name off an email — and the booking being hunted is almost never
   * inside the fortnight the reader happens to have on screen. The API ANDs
   * `quick_search` with whatever else is in the query (filterBookings pushes
   * it into the same `$and`), so sending the date range alongside it turned
   * the search bar into "find this, but only between these dates", which for
   * last month's reference means finding nothing at all.
   *
   * A malformed range — a hand-edited or truncated URL — is left alone: the
   * API ignores anything that is not a pair, so there is nothing to pause,
   * and the notice must not claim otherwise.
   */
  const pausedDateRange = useMemo(() => {
    if (!view.quick_search) return null;
    const { date } = view.filters;
    return Array.isArray(date) && date.length === 2 ? date : null;
  }, [view]);

  /**
   * What the API and the CSV export are actually asked for: the view, minus
   * the date range while a search is running.
   *
   * NOT SENT, not cleared. The range stays in `filters`, and so in the URL
   * and in the Filters panel, because the reader searched FROM a particular
   * fortnight and wants it back the moment the phone call is over; clearing
   * it would make them pick those two dates again every single time, with no
   * undo. It also keeps a shared link honest — `?quick_search=X&filters=…`
   * shows the recipient the same results, the same remembered range and the
   * same notice the sender was looking at.
   *
   * The price is that the screen would otherwise be lying about what it is
   * showing, which is what the notice below and the one in the Filters panel
   * are for. Nothing else is widened: a status, a partner or an operator
   * narrows a reference search usefully and the reader chose it deliberately.
   */
  const effectiveFilters = useMemo(() => {
    if (!pausedDateRange) return filters;
    const { date, ...rest } = filters;
    return rest;
  }, [filters, pausedDateRange]);

  const { exportCsv, isExporting } = useCsvExport('/bookings', effectiveFilters);

  const fetchOrders = () => fetchOrdersWrapper({
    setFiltered,
    setIsLoading,
    setOrders,
    setTotalPages,
    setError,
  })(effectiveFilters);

  const markAsCompleted = (order) => markAsCompletedWrapper({
    setIsLoading,
    fetchOrders,
  })(order);

  const revertToConfirmed = (order) => revertToConfirmedWrapper({
    setIsLoading,
    fetchOrders,
    setError,
  })(order);

  const archiveOrder = (order) => archiveOrderWrapper({
    setIsLoading,
    fetchOrders,
    setError,
  })(order);

  /**
   * The Filters panel and the table hand back a whole filter set with the
   * page and the search mixed into it; split them back into their own
   * params. A new set that names no page means page one — clicking through
   * to the other bookings on an order used to leave you on page 3 of a
   * one-page result, because the old page number survived the new filter.
   */
  const setFilters = useCallback((next) => {
    const { page: nextPage, quick_search: nextQuickSearch, ...rest } = next || {};
    setView({
      filters: rest,
      page: nextPage || 1,
      quick_search: nextQuickSearch || '',
    });
  }, [setView]);

  const sortFn = (column, desc = true) => {
    setFilters({ ...filters, sort: { [column]: desc ? 'desc' : 'asc' } });
  };

  // The pager writes straight into the URL. Scrolling to the top of the new
  // page belongs to the table, next to the buttons the reader turns it with.
  const setPage = useCallback((nextPage) => setView({ page: nextPage }), [setView]);

  /**
   * The search the reader sees and the search in the URL are two things, and
   * either can move first. `committed` is the last value this screen itself
   * wrote, so the echo of the reader's own typing is ignored while a search
   * arriving from anywhere else — the header's global search navigates here
   * with one — is taken up and shown in the box.
   */
  const committed = useRef(view.quick_search);

  const commitQuickSearch = useMemo(() => debounce((value) => {
    committed.current = value;
    setView({ quick_search: value, page: 1 });
  }, QUICK_SEARCH_DEBOUNCE_MS), [setView]);

  useEffect(() => () => commitQuickSearch.cancel(), [commitQuickSearch]);

  useEffect(() => {
    if (view.quick_search === committed.current) return;
    committed.current = view.quick_search;
    setQuickSearch(view.quick_search);
  }, [view.quick_search]);

  const handleQuickSearchChange = (e) => {
    const searchValue = e.target.value;
    setQuickSearch(searchValue);
    commitQuickSearch(searchValue.trim());
  };

  const clearQuickSearch = () => {
    setQuickSearch('');
    commitQuickSearch('');
    commitQuickSearch.flush();
  };

  const debouncedFetchOrders = debounce(() => {
    fetchOrders();
  }, FETCH_DEBOUNCE_MS);

  useEffect(() => {
    // Call the debounced version within useEffect
    debouncedFetchOrders();

    // Cleanup function to cancel any pending debounced call if the component
    // unmounts or before the next effect runs
    return () => debouncedFetchOrders.cancel();
  }, [effectiveFilters]);

  /**
   * `?booking=<id>&tab=<tab>` is a command, not a view: the bell and the
   * trip-claim links use it to open one booking. Obey it once and take those
   * two params back out — Back should return to the list, not re-open the
   * modal — while leaving the search, the filters and the page beside them
   * untouched. Depending on the VALUES rather than on the whole param bag is
   * what keeps this from firing again every time anything else in the URL
   * moves.
   */
  const bookingParam = params.get('booking');
  const tabParam = params.get('tab');

  useEffect(() => {
    if (!bookingParam) return;
    const tab = tabParam || 'overview';
    BookingService.getBookingById(bookingParam)
      .then((order) => { if (order) setDetailsRequest({ order, tab }); })
      .catch(() => {});
    dropParams(['booking', 'tab']);
  }, [bookingParam, tabParam, dropParams]);

  /**
   * `?new=true` from the header's New Order button is the other one-shot
   * command. `quick_search` needs no handling of its own any more: it IS the
   * durable param the box reads, so arriving with one simply shows that
   * search, and nothing has to be copied out of the URL and then deleted.
   *
   * There is no `?new=expense` beside it, and no New Expense button to send
   * one. An expense is created by choosing the type inside New Order, which
   * is the only door that leads to a form the office can actually fill in —
   * the type toggle is one click, and a second button on two screens was
   * noise for a record the office makes a handful of times a month.
   */
  const newParam = params.get('new');

  useEffect(() => {
    if (newParam !== 'true') return;
    orderInputModal.show();
    dropParams(['new']);
    // The modal's `show` is rebuilt every render, so it cannot be a dependency
    // without re-opening the form on each one.
  }, [newParam, dropParams]);

  if (error) {
    return (
      <Error message={error} />
    );
  }

  return (
    <>
      <h1 className="page-title">Orders</h1>

      {/* Compact toolbar */}
      <div className="daily-toolbar">
        <div className="daily-toolbar-row">

          <div className="daily-controls" style={{ marginLeft: 0 }}>
            <Button size="sm" onClick={debouncedFetchOrders} variant="outline-secondary">
              <i className={`fa fa-sync ${isLoading ? 'fa-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={exportCsv} variant="outline-success" disabled={isExporting}>
              <i className={`fa fa-download ${isExporting ? 'fa-spin' : ''}`} />
              <span className="d-none d-sm-inline ms-1">{isExporting ? 'Exporting…' : 'Export'}</span>
            </Button>
            <Button size="sm" onClick={() => setShowFilters(!showFilters)} variant={filtered ? 'warning' : 'outline-secondary'}>
              <i className="fa fa-filter" />
              <span className="d-none d-sm-inline ms-1">Filters</span>
              {filtered && <span className="badge bg-danger ms-1">!</span>}
            </Button>
            <Button size="sm" onClick={() => orderInputModal.show()} variant="primary">
              <i className="fa fa-plus me-1" />
              <span className="d-none d-sm-inline">New Order</span>
            </Button>
          </div>
        </div>

        <div className="daily-search-row">
          <div className="daily-search-wrap">
            <i className="fa fa-search daily-search-icon" />
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search by name, email, booking ref, phone number..."
              value={quickSearch}
              onChange={handleQuickSearchChange}
              className="daily-search-input"
            />
            {quickSearch && (
              <button
                type="button"
                className="daily-search-clear"
                onClick={clearQuickSearch}
                aria-label="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/*
        The screen says out loud that it is showing rows from outside the
        range the reader picked. Without this the list simply appears to be
        wrong: a booking from March turning up under a date range of October,
        with nothing on screen to explain it.
      */}
      {pausedDateRange && (
        <div className="alert alert-info py-2 px-3 small d-flex align-items-start gap-2" role="status">
          <i className="fa fa-calendar-times mt-1" />
          <span>
            Searching
            {' '}
            <strong>all dates</strong>
            {' '}
            — your
            {' '}
            {formatDateTime(pausedDateRange[0], PAUSED_RANGE_FORMAT)}
            {' – '}
            {formatDateTime(pausedDateRange[1], PAUSED_RANGE_FORMAT)}
            {' '}
            range is paused so the search can reach bookings outside it. Clear
            the search to go back to it. Other filters still apply.
          </span>
        </div>
      )}

      { showFilters && (
        <Filters
          setFilters={setFilters}
          filters={filters}
          pausedDateRange={pausedDateRange}
        />
      )}

      <TripClaimsStandingNotice />

      <OrdersTable
        orders={orders}
        page={page}
        totalPages={totalPages}
        setFilters={setFilters}
        archiveOrder={archiveOrder}
        isLoading={isLoading}
        fetchOrders={fetchOrders}
        markAsCompleted={markAsCompleted}
        revertToConfirmed={revertToConfirmed}
        setPage={setPage}
        sortFn={sortFn}
        orderInputModal={orderInputModal}
        orderDetailsModal={orderDetailsModal}
        operatorInputModal={operatorInputModal}
        driverInputModal={driverInputModal}
        tripConfirmationModal={tripConfirmationModal}
        pickupGuideModal={pickupGuideModal}
        addInvoiceModal={addInvoiceModal}
        detailsRequest={detailsRequest}
      />
    </>
  );
}

export default Orders;
